import { useParams } from "@tanstack/react-router";
import { LoaderIcon, PhoneCall } from "lucide-react";
import { Overview } from "@/components/overview";
import { RecentCalls } from "@/components/recent-calls";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useTenantDashboardQuery } from "@/hooks/queries/use-tenant-dashboard-query";

const CDR_DISABLED_LABEL = "CDR disabled";
const CALLS_PERCENTAGE_MULTIPLIER = 100;

type CdrStatus = "loading" | "enabled" | "disabled";

function DisabledMetric() {
	return (
		<>
			<div className="font-bold text-2xl text-muted-foreground">--</div>
			<p className="text-muted-foreground text-xs">{CDR_DISABLED_LABEL}</p>
		</>
	);
}

function LoadingCdrData({ minHeight = "h-[200px]" }: { minHeight?: string }) {
	return (
		<div
			className={`flex ${minHeight} items-center justify-center text-center text-muted-foreground`}
		>
			<div className="flex items-center gap-2 text-sm">
				<LoaderIcon className="h-4 w-4 animate-spin" />
				<span>Loading CDR data...</span>
			</div>
		</div>
	);
}

function NumbersCard({
	totalNumbers,
	uniquePrefixes,
}: {
	totalNumbers: number;
	uniquePrefixes: number;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="font-medium text-sm">Total Numbers</CardTitle>
				<svg
					className="h-4 w-4 text-muted-foreground"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
				</svg>
			</CardHeader>
			<CardContent>
				<div className="font-bold text-2xl">{totalNumbers}</div>
				<p className="text-muted-foreground text-xs">
					Across {uniquePrefixes} prefixes
				</p>
			</CardContent>
		</Card>
	);
}

function AnswerRateCard({
	cdrEnabled,
	todayAnswerRate,
	answerRateDifference,
}: {
	cdrEnabled: boolean;
	todayAnswerRate: number;
	answerRateDifference: number;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="font-medium text-sm">Answer Rate Today</CardTitle>
				<svg
					className="h-4 w-4 text-muted-foreground"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect height="14" rx="2" width="20" x="2" y="5" />
					<path d="M2 10h20" />
				</svg>
			</CardHeader>
			<CardContent>
				{cdrEnabled ? (
					<>
						<div className="font-bold text-2xl">{todayAnswerRate}%</div>
						<p className="text-muted-foreground text-xs">
							{answerRateDifference >= 0 ? "+" : ""}
							{answerRateDifference}% from yesterday
						</p>
					</>
				) : (
					<DisabledMetric />
				)}
			</CardContent>
		</Card>
	);
}

function formatCallsChange(
	callsDifference: number,
	previousCount: number,
): string {
	if (previousCount === 0) {
		return "No calls yesterday";
	}

	const percentageChange = Math.round(
		(callsDifference / previousCount) * CALLS_PERCENTAGE_MULTIPLIER,
	);
	const prefix = percentageChange >= 0 ? "+" : "";
	return `${prefix}${percentageChange}% from yesterday`;
}

function CallsTodayCard({
	cdrEnabled,
	todayCallsCount,
	yesterdayCallsCount,
	callsDifference,
}: {
	cdrEnabled: boolean;
	todayCallsCount: number;
	yesterdayCallsCount: number;
	callsDifference: number;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="font-medium text-sm">Total Calls Today</CardTitle>
				<PhoneCall className="h-4 w-4 text-muted-foreground" />
			</CardHeader>
			<CardContent>
				{cdrEnabled ? (
					<>
						<div className="font-bold text-2xl">
							{todayCallsCount.toLocaleString()}
						</div>
						<p className="text-muted-foreground text-xs">
							{formatCallsChange(callsDifference, yesterdayCallsCount)}
						</p>
					</>
				) : (
					<DisabledMetric />
				)}
			</CardContent>
		</Card>
	);
}

function DashboardStatGrid({
	totalNumbers,
	uniquePrefixes,
	cdrEnabled,
	todayAnswerRate,
	answerRateDifference,
	todayCallsCount,
	yesterdayCallsCount,
	callsDifference,
}: {
	totalNumbers: number;
	uniquePrefixes: number;
	cdrEnabled: boolean;
	todayAnswerRate: number;
	answerRateDifference: number;
	todayCallsCount: number;
	yesterdayCallsCount: number;
	callsDifference: number;
}) {
	return (
		<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
			<NumbersCard
				totalNumbers={totalNumbers}
				uniquePrefixes={uniquePrefixes}
			/>
			<AnswerRateCard
				answerRateDifference={answerRateDifference}
				cdrEnabled={cdrEnabled}
				todayAnswerRate={todayAnswerRate}
			/>
			<CallsTodayCard
				callsDifference={callsDifference}
				cdrEnabled={cdrEnabled}
				todayCallsCount={todayCallsCount}
				yesterdayCallsCount={yesterdayCallsCount}
			/>
		</div>
	);
}

function DashboardCharts({
	cdrStatus,
	tenantId,
}: {
	cdrStatus: CdrStatus;
	tenantId: string;
}) {
	const cdrEnabled = cdrStatus === "enabled";
	const cdrLoading = cdrStatus === "loading";

	return (
		<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
			<Card>
				<CardHeader>
					<CardTitle>Daily Calls</CardTitle>
					<CardDescription>Call volume for the last 7 days</CardDescription>
				</CardHeader>
				<CardContent className="p-0">
					{cdrLoading ? (
						<LoadingCdrData />
					) : cdrEnabled ? (
						<Overview />
					) : (
						<div className="flex h-[200px] items-center justify-center text-center text-muted-foreground">
							<div>
								<p className="mb-2">Call Detail Records are disabled.</p>
								<p className="text-sm">
									Enable CDR in Settings to view call volume data.
								</p>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>Recent Activity</CardTitle>
					<CardDescription>Most recently updated numbers</CardDescription>
				</CardHeader>
				<CardContent>
					{cdrLoading ? (
						<LoadingCdrData minHeight="h-[400px]" />
					) : cdrEnabled ? (
						<RecentCalls skipCdrCheck tenantId={tenantId} />
					) : (
						<div className="py-10 text-center text-muted-foreground">
							Call Detail Records are currently disabled. Enable them in
							Settings.
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

export default function Dashboard() {
	const { tenantId } = useParams({ from: "/_app/$tenantId/" });
	const { data: dashboardData, isLoading: isDashboardLoading } =
		useTenantDashboardQuery(tenantId);

	if (!tenantId) {
		return null;
	}

	const totalNumbers = dashboardData?.numbers?.total ?? 0;
	const uniquePrefixes = dashboardData?.numbers?.uniquePrefixes ?? 0;

	const cdrEnabled = dashboardData?.cdr?.enabled ?? false;
	const cdrStatus: CdrStatus = isDashboardLoading
		? "loading"
		: cdrEnabled
			? "enabled"
			: "disabled";
	const todayCallsCount = dashboardData?.cdr?.today?.calls ?? 0;
	const yesterdayCallsCount = dashboardData?.cdr?.yesterday?.calls ?? 0;
	const callsDifference = dashboardData?.cdr?.deltas?.calls ?? 0;
	const todayAnswerRate = dashboardData?.cdr?.today?.answerRate ?? 0;
	const answerRateDifference = dashboardData?.cdr?.deltas?.answerRate ?? 0;

	return (
		<div className="flex-1 space-y-4 p-4 pt-4 md:p-8">
			<div className="flex items-center justify-between">
				<h2 className="font-bold text-3xl tracking-tight">Overview</h2>
			</div>
			<div className="space-y-4">
				<DashboardStatGrid
					answerRateDifference={answerRateDifference}
					callsDifference={callsDifference}
					cdrEnabled={cdrEnabled}
					todayAnswerRate={todayAnswerRate}
					todayCallsCount={todayCallsCount}
					totalNumbers={totalNumbers}
					uniquePrefixes={uniquePrefixes}
					yesterdayCallsCount={yesterdayCallsCount}
				/>
				<DashboardCharts cdrStatus={cdrStatus} tenantId={tenantId} />
			</div>
		</div>
	);
}
